import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { contentReferencesAreEqual } from '@gitbook/app-content-refs';
import { resolveURLToNavigation } from '@gitbook/app-navigation';
import { INLINE_LINK } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { getEventTransfer, type Plugin } from '@gitbook/slate-react';
import { isHttpUrl, isMailToUrl } from '@gitbook/util-url';

import {
    resolveScreenToContentRefCache,
    useContentReferenceContext,
} from '../../ContentReferences';
import { getAnalyticsPasteEventProps } from '../common';
import type { ContentEditorContextValue } from '../contexts';

/**
 * Create slate plugin to handle paste of urls.
 */
export function usePasteURLPlugin(editorContext: ContentEditorContextValue): Plugin {
    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();

    const { logAnalyticsEvent } = editorContext;
    return React.useMemo(() => {
        return {
            /**
             * When pasting a url:
             *  - If text is selected, it wrap selection in a link
             *  - If selection is collapsed, it insert a link with the url as text
             */
            onPaste: (event, change, editor) => {
                const transfer = getEventTransfer(event);

                if (
                    !(
                        (transfer.type === 'text' || transfer.type === 'html') &&
                        typeof transfer.text === 'string' &&
                        (isHttpUrl(transfer.text) || isMailToUrl(transfer.text))
                    )
                ) {
                    return;
                }

                const url = transfer.text;
                const { value } = change;

                if (value.isCollapsed) {
                    if (value.isInVoidBlock) {
                        return;
                    }

                    // If cursor is collapsed, we insert the link with the url as text
                    change.insertText(url).extend(-url.length);
                }

                const ref: api.ContentRef = { kind: 'url', url };

                change.wrapInline({
                    type: INLINE_LINK,
                    data: { ref },
                });

                // Find all inlines that have been created by this wrap:
                const links = change.value.inlines.filter(
                    (inline) =>
                        inline.type === INLINE_LINK &&
                        inline.data.has('ref') &&
                        contentReferencesAreEqual(inline.data.get('ref'), ref)
                );

                change.collapseToEnd();

                // When the url is from the app itself, we resolve it to a content-ref and update the links
                const screen = resolveURLToNavigation(url);
                if (screen) {
                    resolveScreenToContentRefCache
                        .readAsync([hiveClient, screen, contentReferenceContext])
                        .then(([result]) => {
                            if (result) {
                                editor.change((change) => {
                                    links.forEach((link) => {
                                        const node = change.value.document.getByKey(link.key);
                                        if (node) {
                                            change.setDataByKey(node.key, ['ref'], result);
                                        }
                                    });
                                });
                            }
                        });
                }

                logAnalyticsEvent(getAnalyticsPasteEventProps(event, 'link'));

                return change;
            },
        };
    }, [contentReferenceContext, hiveClient]);
}
